import React, { useState } from "react";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import CurrencyInput from "../../../ui/CurrencyInput";
import Dropdown from "../../../ui/SelectDropdown";
import Button from "../../../ui/Button";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import ModalInput from "../../../ui/ModalTextInput";
import { transactionFeeSchema } from "./schemas";
import { updateTransactionFeeSettingsThunk } from "../../../../redux/features/settingsSlice";
import { toast } from "react-toastify";

interface TransactionFeeModalProps {
  open: boolean;
  onClose: () => void;
}

const feeTypeOptions = [
  { label: "PURCHASE", value: "PURCHASE" },
  { label: "FUNDING", value: "FUNDING" },
  { label: "WITHDRAWAL", value: "WITHDRAWAL" },
];

const TransactionFeeModal: React.FC<TransactionFeeModalProps> = ({
  open,
  onClose,
}) => {
  const { loading } = useAppSelector((state) => state.settings);
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState({
    feeType: "",
    basePercentage: "",
    reducedPercentage: "",
    valueCap: "",
  });

  const [errors, setErrors] = useState<any>({
    basePercentage: "",
    reducedPercentage: "",
    valueCap: "",
  });

  const handleDiscard = () => {
    setFormData({
      feeType: "",
      basePercentage: "",
      reducedPercentage: "",
      valueCap: "",
    });
    setErrors({
      basePercentage: "",
      reducedPercentage: "",
      valueCap: "",
    });
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev: any) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async () => {
    const cleanedData = {
      feeType: formData.feeType,
      basePercentage: parseFloat(formData.basePercentage),
      reducedPercentage: parseFloat(formData.reducedPercentage),
      valueCap: parseFloat(formData.valueCap.replace(/,/g, "")),
    };

    try {
      await transactionFeeSchema.validate(cleanedData, { abortEarly: false });
      dispatch(updateTransactionFeeSettingsThunk(cleanedData))
        .unwrap()
        .then(() => {
          toast.success("Transaction fee settings updated successfully");
          handleDiscard();
        });
    } catch (validationError: any) {
      const fieldErrors: Record<string, string> = {};
      if (validationError.inner) {
        validationError.inner.forEach((err: any) => {
          if (err.path) {
            fieldErrors[err.path] = err.message;
          }
        });
        setErrors(fieldErrors);
      }
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Set Transaction Fee"
      open={open}
      onClose={handleDiscard}
      footer={
        <div className="flex justify-between">
          <Button
            label="Cancel"
            onClick={handleDiscard}
            layout="inline"
            variant="ghost"
          />
          <Button
            label="Save"
            onClick={handleSubmit}
            isLoading={loading}
            layout="inline"
          />
        </div>
      }
    >
      <div className="p-6 space-y-4">
        <Dropdown
          label="Fee Type"
          options={feeTypeOptions}
          value={formData.feeType}
          onSelect={(val) => setFormData((prev) => ({ ...prev, feeType: val }))}
          placeholder="Select fee type"
          error={errors.feeType}
          required
        />

        <ModalInput
          label="Base Percentage (%)"
          name="basePercentage"
          type="number"
          value={formData.basePercentage}
          onChange={handleInputChange}
          error={errors.basePercentage}
          required
        />

        <ModalInput
          label="Reduced Percentage (%)"
          name="reducedPercentage"
          type="number"
          value={formData.reducedPercentage}
          onChange={handleInputChange}
          error={errors.reducedPercentage}
          required
        />

        <CurrencyInput
          label="Value Cap"
          value={formData.valueCap}
          onChange={(val) =>
            setFormData((prev) => ({ ...prev, valueCap: val }))
          }
          placeholder="Enter value cap"
          error={errors.valueCap}
        />
      </div>
    </CustomModalContainer>
  );
};

export default TransactionFeeModal;
